import { FC } from 'react';
import {
  DashboardContainer, DashboardContainerBody, DashboardContainerHeader, ResolvedText, ScrollableList, StyledTitle
} from '../MoaDashboard.styles';
import { ExtractionsContainerProps } from './ExtractionsContainer.types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import { ExtractJobStatus } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams } from 'ag-grid-community';
import ExtractMetricsDisplay from "../ExtractMetricsDisplay";
import BatchStatusDisplay from "../BatchStatusDisplay";

const ExtractionsContainer: FC<ExtractionsContainerProps> = (
    {
      extracts,
      setDrawerColumnDefs,
      setOpenDrawer,
      setDrawerType,
      setFetchExtractionsFailure,
      setExtractionAndActivationsJobId,
      setRequestUserName
    }
) => {
  const muiTheme = createTheme();

  const extractionsColumnDefs = [
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'label',
      headerName: 'Store#',
      sortable: true,
      floatingFilter: false
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'marketNo',
      headerName: 'FZ Code',
      sortable: false,
      floatingFilter: false
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'isResolved',
      headerName: '',
      sortable: false,
      floatingFilter: false,
      cellRenderer: (params: any) => {
        if (params.value) {
          return <ResolvedText>Resolved</ResolvedText>;
        }
        else {
          return '';
        }
      }
    }
  ];

  const couponsColumnDefs = [
    {
      minWidth: 100,
      field: 'label',
      headerName: 'Coupon Group',
      sortable: true,
      floatingFilter: false
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    }
  ];

  const openExtractJobStatusDrawer = (progressMetrics: ExtractJobStatus) => {
    switch (progressMetrics.extractType) {
      case 'COUPONS':
        setDrawerType('couponsExtractions');
        setDrawerColumnDefs(couponsColumnDefs);
        setExtractionAndActivationsJobId(progressMetrics.jobId);
        setFetchExtractionsFailure(true);
        setOpenDrawer(true);
        return;
      case 'EXTRACT':
        setDrawerType('extract');
        setDrawerColumnDefs(extractionsColumnDefs);
        setExtractionAndActivationsJobId(progressMetrics.jobId);
        setRequestUserName(progressMetrics.userName);
        setFetchExtractionsFailure(true);
        setOpenDrawer(true);
        return;
      case 'ACTIVATE':
        setDrawerType('activate');
        setDrawerColumnDefs(extractionsColumnDefs);
        setExtractionAndActivationsJobId(progressMetrics.jobId);
        setRequestUserName(progressMetrics.userName);
        setFetchExtractionsFailure(true);
        setOpenDrawer(true);
        return;
      default:
        return;
    }
  };

  const inProgressExtractMetricsDisplay = () => {
    return (
        <ScrollableList data-testid="extractInProgressContainer" style={{ height: '160px' }}>
          {extracts?.inProgress.length === 0
              ? <StyledTitle>No extractions in progress</StyledTitle>
              : extracts?.inProgress.map((inProgressMetrics) =>
                  (
                      <BatchStatusDisplay
                          key={`extract-in-progress-${inProgressMetrics.jobId}`}
                          name={`${inProgressMetrics.extractType} for ${inProgressMetrics.userName}`}
                          batchStatus={inProgressMetrics}
                          openFailedDrawer={() => openExtractJobStatusDrawer(inProgressMetrics)}
                      />
                  )
              )
          }
        </ScrollableList>
    );
  };

  return (
      <DashboardContainer data-test={'extractionsContainer'}>
        <DashboardContainerHeader data-testid="extractionsContainerHeader">Extractions</DashboardContainerHeader>
        <DashboardContainerBody style={{ gap: 0 }}>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            {inProgressExtractMetricsDisplay()}
            <Divider orientation="horizontal" variant={'fullWidth'} flexItem/>
            <ExtractMetricsDisplay
                extractMetrics={extracts?.upNext ?? []}
                title={'Up next'}
                emptyText={'No extractions waiting'}
                openDrawer={openExtractJobStatusDrawer}
                testName={'upNextExtract'}
            />
            <Divider orientation="horizontal" variant={'fullWidth'} flexItem/>
            <ExtractMetricsDisplay
                extractMetrics={extracts?.done ?? []}
                title={'Done'}
                emptyText={'No extractions'}
                openDrawer={openExtractJobStatusDrawer}
                testName={'doneExtract'}
            />
          </ThemeProvider>
        </DashboardContainerBody>
      </DashboardContainer>
  );
};

export default ExtractionsContainer;
